import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  acceptAll,
  CONSENT_CHANGED_EVENT,
  DEFAULT_CATEGORIES,
  getConsent,
  getDefaultServices,
  OPEN_CONSENT_SETTINGS_EVENT,
  SERVICE_DEFINITIONS,
  setConsent,
  type CategoryKey,
  type ServiceKey,
} from "@/lib/consent";
import { defaultLocale, getLocaleFromPathname } from "@/lib/i18n";
import { getTranslation } from "@/lib/translations";
import { cn } from "@/lib/utils";

export function CookieConsent() {
  const location = useLocation();
  const locale = getLocaleFromPathname(location.pathname);
  const t = getTranslation(locale) as any;
  const cc = t.cookieConsent;

  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [categories, setCategories] =
    useState<Record<CategoryKey, boolean>>(DEFAULT_CATEGORIES);
  const [services, setServices] =
    useState<Record<ServiceKey, boolean>>(getDefaultServices(false));
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const localePrefix = locale === defaultLocale ? "" : `/${locale}`;

  // Initialize state from storage
  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setShowBanner(true);
    } else {
      setCategories(existing.categories);
      setServices(existing.services);
    }
    const onOpen = () => {
      const c = getConsent();
      if (c) {
        setCategories(c.categories);
        setServices(c.services);
      }
      setShowModal(true);
    };
    window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, onOpen);
  }, []);

  // Sync category toggles with their services
  const toggleCategory = (key: CategoryKey, value: boolean) => {
    if (key === "essential") return;
    setCategories((prev) => ({ ...prev, [key]: value }));
    setServices((prev) => {
      const next = { ...prev };
      SERVICE_DEFINITIONS.filter((s) => s.category === key).forEach((s) => {
        next[s.key] = value;
      });
      return next;
    });
  };

  const toggleService = (svc: ServiceKey, value: boolean) => {
    if (svc === "consentManager") return;
    setServices((prev) => {
      const next = { ...prev, [svc]: value };
      // Recompute category state: true if any of its services is on
      const newCats = { ...categories };
      (["functional", "marketing"] as CategoryKey[]).forEach((cat) => {
        const any = SERVICE_DEFINITIONS.filter((s) => s.category === cat).some(
          (s) => next[s.key],
        );
        newCats[cat] = any;
      });
      setCategories(newCats);
      return next;
    });
  };

  const handleAcceptAll = () => {
    acceptAll();
    setCategories({ essential: true, functional: true, marketing: true });
    setServices(getDefaultServices(true));
    setShowBanner(false);
    setShowModal(false);
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT));
  };

  const handleSave = () => {
    setConsent(categories, services);
    setShowBanner(false);
    setShowModal(false);
  };

  const categoryKeys: CategoryKey[] = ["essential", "functional", "marketing"];

  const groupedServices = useMemo(() => {
    return categoryKeys.map((cat) => ({
      cat,
      items: SERVICE_DEFINITIONS.filter((s) => s.category === cat),
    }));
  }, []);

  return (
    <>
      {/* Compact bottom-left banner */}
      {showBanner && !showModal && (
        <div
          role="dialog"
          aria-label={cc.bannerTitle}
          className={cn(
            "fixed z-[60] border border-border bg-background/95 backdrop-blur-md",
            "inset-x-4 bottom-4 sm:inset-x-auto sm:left-4 sm:right-auto sm:bottom-4 sm:max-w-xs",
            "rounded-xl shadow-[0_4px_20px_-6px_rgba(0,0,0,0.15)]",
            "animate-in slide-in-from-bottom duration-500",
          )}
        >
          <div className="p-4">
            <h3 className="text-sm font-medium text-foreground mb-1">
              {cc.bannerTitle}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {cc.bannerText}
            </p>
            <div className="flex gap-4 mt-3 text-xs">
              <Link
                to={`${localePrefix}/privacy`}
                className="text-primary hover:underline"
              >
                {cc.privacyPolicy}
              </Link>
              <Link
                to={`${localePrefix}/imprint`}
                className="text-primary hover:underline"
              >
                {cc.imprint}
              </Link>
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                variant="outline"
                className="flex-1 min-w-0 h-9 px-3 whitespace-normal text-center leading-tight"
                onClick={() => setShowModal(true)}
              >
                {cc.settings}
              </Button>
              <Button
                className="flex-1 min-w-0 h-9 px-3 whitespace-normal text-center leading-tight bg-primary-accessible text-white hover:bg-primary-accessible/90"
                onClick={handleAcceptAll}
              >
                {cc.acceptAll}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
            <DialogTitle className="text-xl font-medium">
              {cc.modalTitle}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
              {cc.modalDescription}
            </DialogDescription>
            <div className="flex gap-4 text-xs pt-1">
              <Link
                to={`${localePrefix}/privacy`}
                className="text-primary hover:underline"
                onClick={() => setShowModal(false)}
              >
                {cc.privacyPolicy}
              </Link>
              <Link
                to={`${localePrefix}/imprint`}
                className="text-primary hover:underline"
                onClick={() => setShowModal(false)}
              >
                {cc.imprint}
              </Link>
            </div>
          </DialogHeader>

          <Tabs defaultValue="categories" className="flex-1 flex flex-col min-h-0">
            <TabsList className="mx-6 mt-4 grid grid-cols-2 bg-muted">
              <TabsTrigger value="categories">{cc.tabCategories}</TabsTrigger>
              <TabsTrigger value="services">{cc.tabServices}</TabsTrigger>
            </TabsList>

            <TabsContent
              value="categories"
              className="flex-1 overflow-y-auto px-6 py-4 space-y-3 mt-0"
            >
              {categoryKeys.map((key) => {
                const cat = cc.categories[key];
                const isEssential = key === "essential";
                const expandedKey = `cat-${key}`;
                const isExpanded = expanded[expandedKey];
                return (
                  <div
                    key={key}
                    className="border border-border rounded-lg bg-card"
                  >
                    <div className="flex items-start gap-4 p-4">
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() =>
                            setExpanded((p) => ({
                              ...p,
                              [expandedKey]: !p[expandedKey],
                            }))
                          }
                          className="flex items-center gap-1.5 font-medium text-foreground"
                        >
                          {cat.name}
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 transition-transform",
                              isExpanded && "rotate-180",
                            )}
                          />
                        </button>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          {cat.description}
                        </p>
                        {isExpanded && (
                          <ul className="mt-3 space-y-1 text-xs text-muted-foreground">
                            {SERVICE_DEFINITIONS.filter(
                              (s) => s.category === key,
                            ).map((s) => (
                              <li key={s.key}>• {cc.services[s.key].name}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="flex items-center pt-1">
                        {isEssential ? (
                          <span className="text-xs text-muted-foreground italic mr-1">
                            {cc.alwaysActive}
                          </span>
                        ) : null}
                        <Switch
                          checked={categories[key]}
                          disabled={isEssential}
                          onCheckedChange={(v) => toggleCategory(key, v)}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </TabsContent>

            <TabsContent
              value="services"
              className="flex-1 overflow-y-auto px-6 py-4 space-y-4 mt-0"
            >
              {groupedServices.map(({ cat, items }) => (
                <div key={cat} className="space-y-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
                    {cc.categories[cat].name}
                  </h4>
                  {items.map((s) => {
                    const svc = cc.services[s.key];
                    const isEssential = s.key === "consentManager";
                    const expandedKey = `svc-${s.key}`;
                    const isExpanded = expanded[expandedKey];
                    return (
                      <div
                        key={s.key}
                        className="border border-border rounded-lg bg-card"
                      >
                        <div className="flex items-start gap-4 p-3">
                          <div className="flex-1 min-w-0">
                            <button
                              onClick={() =>
                                setExpanded((p) => ({
                                  ...p,
                                  [expandedKey]: !p[expandedKey],
                                }))
                              }
                              className="flex items-center gap-1.5 font-medium text-sm text-foreground"
                            >
                              {svc.name}
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 transition-transform",
                                  isExpanded && "rotate-180",
                                )}
                              />
                            </button>
                            {isExpanded && (
                              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                                {svc.description}
                              </p>
                            )}
                          </div>
                          <Switch
                            checked={services[s.key]}
                            disabled={isEssential}
                            onCheckedChange={(v) => toggleService(s.key, v)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </TabsContent>
          </Tabs>

          <div className="flex flex-col sm:flex-row gap-2 px-6 py-4 border-t border-border bg-muted/30">
            <Button
              variant="outline"
              className="flex-1 h-11"
              onClick={handleSave}
            >
              {cc.saveSettings}
            </Button>
            <Button
              className="flex-1 h-11 bg-primary-accessible text-white hover:bg-primary-accessible/90"
              onClick={handleAcceptAll}
            >
              {cc.acceptAll}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
